import { MouseEvent, useCallback } from "react";
import { IRoute } from "./routes";
import { generatePath } from "react-router-dom";
import { IMenuRoute, RouteStore, getTestId } from "./RouteStore";
import { useSingleton } from "../injection";

interface IProps {
    route: IRoute | IMenuRoute;
    pathParams?: unknown;
    customPath?: ICustomPath;
    openInNewTab?: boolean;
}

interface ICustomPath {
    path: string;
    isAbsolute?: boolean;
}

export interface ILink {
    href: string;
    onClick: (event: MouseEvent<HTMLElement>) => void;
    target: string | undefined;
    testId: string;
}

export const useLink = ({
    route,
    pathParams,
    customPath,
    openInNewTab
}: IProps): ILink => {
    const routeStore = useSingleton(RouteStore);

    const path =
        customPath?.path ??
        (route.path
            ? generatePath(
                  route.path,
                  pathParams ?? route.defaultPathParams ?? {}
              )
            : "");

    const target = openInNewTab ? "_blank" : undefined;
    const isAbsolute = customPath?.isAbsolute ?? false;

    // need stable reference to originalRoute for use with access logic (not IMenuRoute)
    const originalRoute =
        "originalRoute" in route && route.originalRoute
            ? route.originalRoute
            : route;

    const onClick = useCallback(
        (event: MouseEvent<HTMLElement>) => {
            if (routeStore.hasAccess(originalRoute)) {
                if (!hasAncestorWithHref(event.target as HTMLElement)) {
                    // onClick is attached to a non-link element, so navigate using location object
                    if (openInNewTab) {
                        window.open(isAbsolute ? path : `#${path}`, "_blank");
                    } else if (isAbsolute) {
                        navigateHref(path);
                    } else {
                        navigateHash(path);
                    }
                }
                return;
            }
            routeStore.triggerAccessCheck(
                originalRoute,
                isAbsolute ? path : `#${path}`,
                target
            );
            event.preventDefault();
        },
        [originalRoute, path, target, isAbsolute, openInNewTab, routeStore]
    );

    const testId = getTestId(route);

    return {
        href: openInNewTab ? path : `#${path}`,
        onClick,
        testId,
        target
    };
};

function hasAncestorWithHref(element: HTMLElement | null): boolean {
    while (element && element instanceof HTMLElement) {
        if (element.hasAttribute("href")) {
            return true; // Found an ancestor with href attribute
        }
        element = element.parentElement; // Move to the parent element
    }
    return false; // No ancestor with href attribute found
}

const navigateHref = (path: string) => (window.location.href = path);
const navigateHash = (path: string) => (window.location.hash = path);
